/**
 * Per-drone FX bus: voices → dry + sends (ping-pong delay, convolution reverb with shimmer feedback) →
 * mid/side width → drone output → master. Parameters are (re)applied live from the drone document and macros.
 */
import type { Drone } from '@tonist/shared';
import type { WorkletFlags } from './context';
import { PingPongDelay } from './fx/delay';
import { Reverb } from './fx/reverb';
import { Shimmer } from './fx/shimmer';
import { clamp, targetTau, type MacroMap } from './params';

export class DroneGraph {
  readonly input: GainNode;
  readonly output: GainNode;
  private readonly dry: GainNode;
  private readonly delaySend: GainNode;
  private readonly delay: PingPongDelay;
  private readonly reverbSend: GainNode;
  private readonly reverb: Reverb;
  private readonly reverbWet: GainNode;
  private readonly widthIn: GainNode;
  private readonly splitter: ChannelSplitterNode;
  private readonly merger: ChannelMergerNode;
  private readonly wLL: GainNode;
  private readonly wLR: GainNode;
  private readonly wRL: GainNode;
  private readonly wRR: GainNode;
  private shimmer: Shimmer | null = null;
  private shimmerOff: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;

  constructor(private readonly ctx: AudioContext, drone: Drone, macros: MacroMap, private readonly worklets: WorkletFlags) {
    this.input = ctx.createGain();
    this.output = ctx.createGain();
    this.dry = ctx.createGain();
    this.delaySend = ctx.createGain();
    this.delaySend.gain.value = 0;
    this.delay = new PingPongDelay(ctx, drone.space.delay);
    this.reverbSend = ctx.createGain();
    this.reverbSend.gain.value = 0;
    this.reverb = new Reverb(ctx, drone.space.reverb);
    this.reverbWet = ctx.createGain();
    this.reverbWet.gain.value = 0;
    this.widthIn = ctx.createGain();
    this.splitter = ctx.createChannelSplitter(2);
    this.merger = ctx.createChannelMerger(2);
    this.wLL = ctx.createGain(); this.wLR = ctx.createGain(); this.wRL = ctx.createGain(); this.wRR = ctx.createGain();

    this.input.connect(this.dry); this.dry.connect(this.widthIn);
    this.input.connect(this.delaySend); this.delaySend.connect(this.delay.input); this.delay.output.connect(this.widthIn);
    this.input.connect(this.reverbSend); this.reverbSend.connect(this.reverb.input); this.reverb.output.connect(this.reverbWet); this.reverbWet.connect(this.widthIn);

    // mid/side width as a 2x2 gain matrix: L' = L(1+w)/2 + R(1-w)/2, R' = L(1-w)/2 + R(1+w)/2
    this.widthIn.connect(this.splitter);
    this.splitter.connect(this.wLL, 0); this.splitter.connect(this.wLR, 0);
    this.splitter.connect(this.wRL, 1); this.splitter.connect(this.wRR, 1);
    this.wLL.connect(this.merger, 0, 0); this.wRL.connect(this.merger, 0, 0);
    this.wLR.connect(this.merger, 0, 1); this.wRR.connect(this.merger, 0, 1);
    this.merger.connect(this.output);
    this.apply(drone, macros, ctx.currentTime);
  }

  private ensureShimmer(spec: Drone['space']['shimmer']): Shimmer {
    if (this.shimmerOff) { clearTimeout(this.shimmerOff); this.shimmerOff = null; }
    if (!this.shimmer) {
      const s = new Shimmer(this.ctx, spec, this.worklets.pitchshift);
      this.reverb.output.connect(s.input);
      s.feedback.connect(this.reverb.convolverIn);
      s.output.connect(this.widthIn);
      this.shimmer = s;
    }
    return this.shimmer;
  }

  apply(drone: Drone, macros: MacroMap, t: number): void {
    if (this.disposed) return;
    const sp = drone.space;
    const rv = clamp(sp.reverb.mix * macros.spaceScale, 0, 1);
    const dl = clamp(sp.delay.mix * macros.spaceScale, 0, 1);
    const shimmerMix = clamp(Math.max(macros.shimmerMix, sp.shimmer.mix), 0, 1);
    targetTau(this.dry.gain, 1 - 0.35 * Math.max(rv, dl, shimmerMix), t, 0.1);
    targetTau(this.delaySend.gain, dl, t, 0.1);
    targetTau(this.reverbSend.gain, rv > 0 || shimmerMix > 0 ? 1 : 0, t, 0.1);
    targetTau(this.reverbWet.gain, rv, t, 0.1);
    this.reverb.setParams(sp.reverb);
    this.delay.set(sp.delay, t);
    if (shimmerMix > 0) {
      this.ensureShimmer(sp.shimmer).set(sp.shimmer, shimmerMix, t);
    } else if (this.shimmer) {
      this.shimmer.set(sp.shimmer, 0, t);
      if (!this.shimmerOff) {
        this.shimmerOff = setTimeout(() => { this.shimmerOff = null; this.shimmer?.dispose(); this.shimmer = null; }, 4000);
      }
    }
    const w = clamp(sp.width, 0, 2);
    targetTau(this.wLL.gain, (1 + w) / 2, t, 0.1);
    targetTau(this.wRR.gain, (1 + w) / 2, t, 0.1);
    targetTau(this.wLR.gain, (1 - w) / 2, t, 0.1);
    targetTau(this.wRL.gain, (1 - w) / 2, t, 0.1);
  }

  /** Fade the bus out (used before disposal so releasing voices do not click). */
  fadeOut(t: number, seconds: number): void {
    targetTau(this.output.gain, 0, t, seconds / 4.6);
  }

  dispose(): void {
    this.disposed = true;
    if (this.shimmerOff) clearTimeout(this.shimmerOff);
    this.shimmer?.dispose();
    this.delay.dispose();
    this.reverb.dispose();
    for (const n of [this.input, this.output, this.dry, this.delaySend, this.reverbSend, this.reverbWet, this.widthIn, this.splitter, this.merger, this.wLL, this.wLR, this.wRL, this.wRR]) {
      try { n.disconnect(); } catch { /* gone */ }
    }
  }
}
